import { Avent, AventType } from "../util/avent";
import { assert } from "../util/assert";
import { ViewType, View, ViewAventData } from "./view";
import { isSet } from "../util/types";
import { MenuView } from "./menu_view";
import { State } from "./state";
import { getById } from "../util/dom";
import { RouteView } from "./route_view";
import { Transition } from "./transition";
import { historyManager, PUSH } from "@src/util/history_state";

// Hello
const MENU_PATH = location.pathname + '#menu';
const ROUTE_PATH = location.pathname;

export class ViewManager {
  private readonly views: Record<ViewType, View>;
  private transition: Transition | null = null;
  _currentType: ViewType;

  constructor(state: State, requestLocationUpdate: () => void) {
    const isMenuOpen = location.hash === '#menu';

    const menuView = new MenuView(state, requestLocationUpdate, getById('menu'));
    menuView.finishInit(isMenuOpen);
    const routeView = new RouteView(state, requestLocationUpdate, getById('route'));
    routeView.finishInit(!isMenuOpen);

    if (historyManager.state) {
      window.scrollTo(0, historyManager.state.scroll);
    }
    historyManager.enableScrollSaving();

    this.views = {
      [ViewType.MENU]: menuView,
      [ViewType.ROUTE]: routeView,
    };

    this._currentType = isMenuOpen ? ViewType.MENU : ViewType.ROUTE;

    for (const view of Object.values(this.views)) {
      view.addAventListener(AventType.NavigateViews,
        (avent: Avent<ViewAventData>) => this.handleNavigateAvent(avent));
    }

    window.addEventListener('popstate', this.handlePopstate.bind(this));
  }

  protected handleNavigateAvent(avent: Avent<ViewAventData>) {
    assert(isSet(avent.data.newView));
    const newViewType = avent.data.newView;
    if (newViewType === this.currentType) {
      return;
    }

    // Set the new URL.
    if (newViewType === ViewType.MENU) {
      historyManager.update({ type: PUSH, url: MENU_PATH });
    } else {
      assert(newViewType === ViewType.ROUTE);
      if (historyManager.state?.stackSize) {
        // If we've pushed to get here, then trigger a back to go to the route.
        history.back();
        return; // Returning because back() triggers a popstate.
      } else {
        historyManager.update({ type: PUSH, url: ROUTE_PATH });
      }
    }

    // Perform the transition.
    this.navigateTo(avent.data.newView!);
  }

  private navigateTo(newViewType: ViewType, immediateTransition = false) {
    let canceledTransition = false;
    if (this.transition) {
      canceledTransition = this.transition.finish();
    }
    if (newViewType === this.currentType) {
      return;
    }
    const previousView = this.current;
    const newView = this.views[newViewType];
    this._currentType = newViewType;

    this.transition = new Transition(previousView, newView, immediateTransition || canceledTransition);
  }

  private handlePopstate(event: PopStateEvent) {
    // On iOS Safari, the user can go back/forward by swiping from the edge of
    // the screen, and in this case Safari shows the previous page as you swipe.
    // Running an animation in this state looks totally broken.
    const immediateTransition = event.hasUAVisualTransition;
    this.navigateTo(this.getViewFromHash(), immediateTransition);
  }

  private getViewFromHash(): ViewType {
    return window.location.hash === '#menu' ? ViewType.MENU : ViewType.ROUTE;
  }

  get current(): View {
    return this.views[this.currentType];
  }

  get currentType(): ViewType {
    return this._currentType;
  }
}